/* Canvello v8 — motion (design/TOKENS.md §5).
 *
 * The v8 prototype's motion code (Canvello v8.dc.html L1383-1724) as plain DOM
 * code, with the custom cursor turning white over dark grounds as in Pages v7
 * (TOKENS.md disagreement #7). Markup keeps the prototypes' data-* hooks and
 * ids (cvProg, cvRailBar, cvRailN, cvPdpBar, cvBuy), so it ports as drawn.
 *
 * Split of work: cv-motion.css holds every hook's resting and hover state.
 * Hover is plain :hover there, so it works before this file runs and never
 * sticks after a tap. This file does what CSS cannot: scroll position, the
 * observers, counters, the headline split, and whatever follows the pointer.
 *
 * Switches
 *   html[data-cv-motion]   theme setting, 0-100. Scales lift, zoom, tilt, the
 *                          magnet, the card turn and the reveal distance (CSS
 *                          reads it as --cv-motion). At 0, reveal, unveil,
 *                          count, split, tilt, magnet and glow are off.
 *   html[data-cv-cursor]   on | off, theme setting. Fine pointers only.
 *   prefers-reduced-motion nothing is set up and content shows as written.
 *                          Kept, as they move only with the visitor's own
 *                          input: the scroll frame and press-and-hold zoom.
 *
 * One requestAnimationFrame loop: run(job) queues a function for the next
 * frame, and a job that returns true is queued again. The loop stops when
 * nothing is queued, so an idle page costs no frames. Three
 * IntersectionObservers (reveal, unveil, count) and two ResizeObservers
 * (header, page height) feed it.
 *
 * Differences from v8, deliberate:
 *  - Elements reveal through data-cv-rev / data-cv-u states (wait → in →
 *    done) styled in cv-motion.css, and hand their own styles back at "done".
 *  - Counters keep the server's text exactly: the final frame restores it,
 *    and the grouping separator is the one the number was written with.
 *  - The split headline keeps its accessible name (aria-label on the
 *    heading, the word masks aria-hidden).
 *  - The cursor hides when the pointer leaves the window, and its idle loop
 *    stops once the dot and ring have caught up.
 *  - Zoom scales back out from the point it zoomed into; on touch it waits
 *    for a still 220ms hold, so a swipe across the image doesn't zoom.
 *  - The PDP bar leaves the tab order (inert) while it is hidden.
 *
 * Theme editor reloads and anything inserted later (quick view, filtered
 * grids) are picked up with CVMotion.scan(root). A hero that changes its
 * headline calls CVMotion.split(el, text).
 */
(() => {
  if (window.CVMotion) return;

  const doc = document;
  const root = doc.documentElement;
  const matches = (query) => window.matchMedia(query).matches;
  const reduce = matches('(prefers-reduced-motion: reduce)');
  const fine = matches('(hover: hover) and (pointer: fine)');
  const setting = parseFloat(root.getAttribute('data-cv-motion'));
  const mo = reduce ? 0 : Number.isFinite(setting) ? Math.min(1, Math.max(0, setting / 100)) : 1;
  const cursorOn = fine && !reduce && root.getAttribute('data-cv-cursor') !== 'off';

  const $ = (id) => doc.getElementById(id);
  const within = (scope, selector) => {
    const found = Array.from(scope.querySelectorAll(selector));
    if (scope.matches && scope.matches(selector)) found.unshift(scope);
    return found;
  };
  // An element inside a section the browser is skipping (content-visibility:
  // auto, home below the first screen) is far from view by definition: say so
  // without measuring it, which would force that whole section's layout and
  // undo the skip. Its IntersectionObserver unveils it on arrival.
  // (Rendered, but not with the content-visibility check: hidden elements,
  // such as the hero's later chapters, keep the old answer.)
  const skipped = (el) => !!el.checkVisibility && el.checkVisibility() && !el.checkVisibility({ contentVisibilityAuto: true });
  const inView = (el, share) => !skipped(el) && el.getBoundingClientRect().top < window.innerHeight * share;
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

  /* ---- One frame loop ---------------------------------------------------- */

  const jobs = new Set();
  let raf = 0;
  const tick = (time) => {
    const due = Array.from(jobs);
    jobs.clear();
    raf = 0;
    due.forEach((job) => {
      if (job(time)) run(job);
    });
  };
  const run = (job) => {
    jobs.add(job);
    if (!raf) raf = requestAnimationFrame(tick);
  };

  /* ---- Scroll frame (v8:L1460-1476) -------------------------------------- */
  // Progress bar, rail bar and number, the header's scrolled state (header CSS
  // shrinks its padding 19 → 13px on html[data-cv-scrolled]), the header's
  // live height and bottom edge for sticky offsets, and the PDP buy bar.

  let header = null;
  let headerRO = null;
  let maxY = 0;
  const last = { r: -1, n: '', scrolled: null, bottom: -1, buy: null };

  const measure = () => {
    maxY = Math.max(0, root.scrollHeight - window.innerHeight);
  };

  const frame = () => {
    const y = window.scrollY;
    const r = maxY > 0 ? clamp(y / maxY, 0, 1) : 0;
    if (r !== last.r) {
      last.r = r;
      const prog = $('cvProg');
      const bar = $('cvRailBar');
      const num = $('cvRailN');
      if (prog) prog.style.transform = `scaleX(${r.toFixed(4)})`;
      if (bar) bar.style.transform = `scaleY(${r.toFixed(3)})`;
      const n = String(Math.round(r * 100)).padStart(2, '0');
      if (num && n !== last.n) num.textContent = last.n = n;
    }
    const scrolled = y > 24;
    if (scrolled !== last.scrolled) root.toggleAttribute('data-cv-scrolled', (last.scrolled = scrolled));
    if (header) {
      const bottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
      if (bottom !== last.bottom) root.style.setProperty('--cv-header-bottom', (last.bottom = bottom) + 'px');
    }
    const buyBar = $('cvPdpBar');
    const buy = $('cvBuy');
    if (buyBar && buy) {
      const on = buy.getBoundingClientRect().bottom < 58;
      if (on !== last.buy) {
        buyBar.toggleAttribute('data-cv-on', (last.buy = on));
        buyBar.inert = !on;
      }
    }
    return false;
  };

  const bindHeader = () => {
    const next = doc.querySelector('[data-cv-header]') || doc.querySelector('.section-header');
    if (next === header) return;
    if (headerRO) headerRO.disconnect();
    header = next;
    last.bottom = -1;
    if (!header || !('ResizeObserver' in window)) return;
    headerRO = new ResizeObserver(() => {
      root.style.setProperty('--cv-header-h', Math.round(header.getBoundingClientRect().height) + 'px');
      run(frame);
    });
    headerRO.observe(header);
  };

  /* ---- data-rev: fade up on scroll (v8:L1499-1516) ----------------------- */
  // Anything already in the top 94 % of the window is left alone.

  const settle = (el, key, ms) => setTimeout(() => (el.dataset[key] = 'done'), ms);

  const showRev = (el) => {
    if (el.dataset.cvRev !== 'wait') return;
    revIO.unobserve(el);
    el.dataset.cvRev = 'in';
    settle(el, 'cvRev', 1400);
  };

  const revIO = mo
    ? new IntersectionObserver((entries) => entries.forEach((e) => e.isIntersecting && showRev(e.target)), {
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.03,
      })
    : null;

  // els and near (in view at load) come from scan(), which reads every
  // position before anything is written: one layout, not one per element.
  const initReveal = (els, near) =>
    els.forEach((el, i) => {
      if (!mo || near[i]) {
        el.dataset.cvRev = 'done';
        return;
      }
      el.dataset.cvRev = 'wait';
      revIO.observe(el);
      setTimeout(() => showRev(el), 9000);
    });

  /* ---- data-unveil: image wipes down from the top (v8:L1478-1497) -------- */
  // cv-motion.css clips these from first paint, so an image in view never
  // flashes before the script runs.

  const showUnveil = (el) => {
    if (el.dataset.cvU !== 'wait') return;
    if (unveilIO) unveilIO.unobserve(el);
    el.dataset.cvU = 'in';
    settle(el, 'cvU', 1700);
  };

  // Threshold 0, not v8's .08: Chrome measures the target through its own
  // clip-path, and a clipped-shut element never reaches 8 %. In v8 only the
  // 9s fallback revealed these. At 0 it fires as the top edge enters.
  const unveilIO = mo
    ? new IntersectionObserver((entries) => entries.forEach((e) => e.isIntersecting && showUnveil(e.target)), {
        rootMargin: '0px 0px -6% 0px',
        threshold: 0,
      })
    : null;

  const initUnveil = (els, near) =>
    els.forEach((el, i) => {
      if (!mo) {
        el.dataset.cvU = 'done';
        return;
      }
      el.dataset.cvU = 'wait';
      if (near[i]) {
        setTimeout(() => showUnveil(el), 60);
        return;
      }
      unveilIO.observe(el);
      setTimeout(() => showUnveil(el), 9000);
    });

  /* ---- data-count: number counts up (v8:L1518-1541) ---------------------- */
  // 1150ms, easeOutQuart, from to−24 above 1900, else from 0.

  const NUMBER = /^(?:\d{1,3}(?:([,.  ٬ ])\d{3})+|\d+)$/;

  const count = (el) => {
    const text = el.textContent.trim();
    const m = NUMBER.exec(text);
    if (!m) return;
    const sep = m[1] || '';
    const to = parseInt(text.replace(/\D/g, ''), 10);
    const from = to > 1900 ? to - 24 : 0;
    const format = (v) => (sep ? String(v).replace(/\B(?=(\d{3})+(?!\d))/g, sep) : String(v));
    const start = performance.now();
    el.textContent = format(from);
    run(function step(time) {
      const k = clamp((time - start) / 1150, 0, 1);
      el.textContent = k < 1 ? format(Math.round(from + (to - from) * (1 - Math.pow(1 - k, 4)))) : text;
      return k < 1;
    });
  };

  const countIO = mo
    ? new IntersectionObserver(
        (entries) =>
          entries.forEach((e) => {
            if (!e.isIntersecting) return;
            countIO.unobserve(e.target);
            count(e.target);
          }),
        { threshold: 0.5 }
      )
    : null;

  const initCount = (els, near) =>
    els.forEach((el, i) => {
      el.dataset.cvC = '1';
      if (!mo) return;
      if (near[i]) count(el);
      else countIO.observe(el);
    });

  /* ---- data-splith: headline rises word by word (v8:L1543-1564) ---------- */
  // Each word in an overflow mask, 68ms apart. Words follow the text's own
  // order, so Arabic rises right to left; unspaced Chinese rises as one line.

  const split = (el, text) => {
    const words = String(text == null ? el.textContent : text).trim();
    el.dataset.cvSplit = 'set';
    if (!mo || !words) {
      if (text != null) el.textContent = words;
      el.removeAttribute('aria-label');
      return;
    }
    el.setAttribute('aria-label', words);
    el.textContent = '';
    words.split(/\s+/).forEach((w, i, list) => {
      const mask = doc.createElement('span');
      const word = doc.createElement('span');
      mask.className = 'cv-split';
      mask.setAttribute('aria-hidden', 'true');
      word.textContent = w;
      word.style.transitionDelay = i * 68 + 'ms';
      mask.appendChild(word);
      el.appendChild(mask);
      if (i < list.length - 1) el.appendChild(doc.createTextNode(' '));
    });
    void el.offsetWidth; // commit the lowered words so they transition up
    el.dataset.cvSplit = 'in';
  };

  const initSplit = (scope) => within(scope, '[data-splith]:not([data-cv-split])').forEach((el) => split(el));

  /* ---- Pointer: cursor, magnet, tilt, glow, card turn (v8:L1566-1679) ---- */

  let px = 0;
  let py = 0;
  let target = null;
  let mag = null;
  let tilt = null;
  let lume = null;
  let turn = null;

  // Read every rectangle first, then write, so one frame costs one layout.
  const pointerJob = () => {
    const t = target && target.closest ? target : null;
    const nextMag = mo && t ? t.closest('[data-mag]') : null;
    const nextTilt = mo && t ? t.closest('[data-tilt]') : null;
    const nextLume = mo && t ? t.closest('[data-lume]') : null;
    const card = mo && t ? t.closest('[data-card]') : null;
    const nextTurn = card && card.querySelector('[data-turn]');
    const well = nextTurn && (nextTurn.closest('[data-unveil]') || nextTurn.parentElement);
    const glow = nextLume && nextLume.querySelector('[data-lumeg]');

    const rMag = nextMag && nextMag.getBoundingClientRect();
    const rTilt = nextTilt && nextTilt.getBoundingClientRect();
    const rLume = glow && nextLume.getBoundingClientRect();
    const rWell = well && well.getBoundingClientRect();
    const half = glow ? glow.offsetWidth / 2 : 0;

    if (mag && mag !== nextMag) mag.style.transform = '';
    if (tilt && tilt !== nextTilt) tilt.style.transform = '';
    if (turn && turn !== nextTurn) turn.style.transform = '';
    mag = nextMag;
    tilt = nextTilt;
    lume = nextLume;
    turn = nextTurn;

    if (rMag) {
      // 0.08 of the distance from the centre, at most 3px.
      const k = 0.08 * mo;
      const dx = clamp((px - (rMag.left + rMag.width / 2)) * k, -3, 3);
      const dy = clamp((py - (rMag.top + rMag.height / 2)) * k, -3, 3);
      mag.style.transform = `translate3d(${dx.toFixed(1)}px,${dy.toFixed(1)}px,0)`;
    }
    if (rTilt) {
      // ±3° toward the pointer at full motion.
      const rx = ((py - (rTilt.top + rTilt.height / 2)) / rTilt.height) * -3 * mo;
      const ry = ((px - (rTilt.left + rTilt.width / 2)) / rTilt.width) * 3 * mo;
      tilt.style.transform = `perspective(1200px) rotateX(${rx.toFixed(2)}deg) rotateY(${ry.toFixed(2)}deg)`;
    }
    if (rLume) {
      glow.style.transform = `translate3d(${(px - rLume.left - half).toFixed(0)}px,${(py - rLume.top - half).toFixed(0)}px,0)`;
    }
    if (rWell && rWell.width && rWell.height) {
      // The second image drifts against the pointer, ±4px.
      const x = (px - rWell.left) / rWell.width - 0.5;
      const y = (py - rWell.top) / rWell.height - 0.5;
      turn.style.transform = `translate3d(${(-x * 8 * mo).toFixed(1)}px,${(-y * 8 * mo).toFixed(1)}px,0) scale(1.03)`;
    }
    return false;
  };

  // Custom cursor: v8's lerp (dot .2, ring .1 per frame), white over
  // [data-onDark] grounds as in P7. The word comes from the nearest [data-cur].
  let cur = null;
  let ring = null;
  let label = null;
  let cx = 0;
  let cy = 0;
  let rx = 0;
  let ry = 0;
  let shown = false;
  let lastOver = null;

  const cursorJob = () => {
    cx += (px - cx) * 0.2;
    cy += (py - cy) * 0.2;
    rx += (px - rx) * 0.1;
    ry += (py - ry) * 0.1;
    cur.style.transform = `translate3d(${cx.toFixed(1)}px,${cy.toFixed(1)}px,0)`;
    ring.style.transform = `translate3d(${rx.toFixed(1)}px,${ry.toFixed(1)}px,0)`;
    return Math.abs(px - rx) + Math.abs(py - ry) > 0.2;
  };

  const showCursor = (on) => {
    if (on === shown) return;
    shown = on;
    cur.toggleAttribute('data-show', on);
    ring.toggleAttribute('data-show', on);
  };

  const dressCursor = (el) => {
    if (el === lastOver) return;
    lastOver = el;
    const host = el && el.closest ? el.closest('[data-cur], [data-tip]') : null;
    const word = host ? host.getAttribute('data-cur') || host.getAttribute('data-tip') : '';
    const dark = !!(el && el.closest && el.closest('[data-ondark]'));
    [cur, ring].forEach((n) => {
      n.toggleAttribute('data-word', !!word);
      n.toggleAttribute('data-dark', dark);
    });
    if (word) label.textContent = word;
  };

  if (cursorOn) {
    ring = doc.createElement('div');
    cur = doc.createElement('div');
    label = doc.createElement('span');
    ring.className = 'cv-ring';
    cur.className = 'cv-cur';
    label.className = 'cv-cur__t';
    ring.setAttribute('aria-hidden', 'true');
    cur.setAttribute('aria-hidden', 'true');
    cur.appendChild(label);
    doc.body.append(ring, cur);
    doc.body.classList.add('cv-nc');
  }

  if (fine && !reduce) {
    window.addEventListener(
      'mousemove',
      (e) => {
        px = e.clientX;
        py = e.clientY;
        target = e.target;
        run(pointerJob);
        if (!cur) return;
        if (!shown) {
          cx = rx = px;
          cy = ry = py;
        }
        showCursor(true);
        dressCursor(target);
        run(cursorJob);
      },
      { passive: true }
    );
    // Leaving the window: release the magnet and tilt, hide the cursor.
    doc.addEventListener('mouseout', (e) => {
      if (e.relatedTarget) return;
      target = null;
      run(pointerJob);
      if (cur) showCursor(false);
    });
  }

  /* ---- Pointer label (2026-10-19) ---------------------------------------- */
  // With the system pointer kept (custom cursor off), the nearest [data-cur]'s
  // word rides in a small tag just under the pointer's tip. It waits a beat
  // before it shows, so a sweep across the page flashes nothing; follows with
  // a soft lag; between two labelled elements it stays up and eases to the
  // next word's width instead of blinking; dips on press and picks up a word
  // that changed with the click (Save → Saved); flips at the window's edges;
  // turns light over [data-ondark] grounds; steps aside while typing.
  // Reduced motion: the same tag, placed at once, with no easing.
  //
  // Only where it tells the visitor something (2026-10-19):
  //   [data-tip]    a choice whose name isn't on screen (swatches): the name,
  //                 in sentence case; the dot is filled on the current choice
  //                 and a ring on the others
  //   .cv-btn       the primary buttons (Add to bag, Checkout, Send my offer)
  //   [data-card]   a whole tile that is one link: journal posts, collection
  //                 tiles, the next article, the Continue band
  //   [data-cur] on a control with no visible text (♡, icons, thumbnails,
  //                 the product photo), or marked [data-cur-show]
  // A text link or menu item already says what it does: no tag there.
  const tipOn = fine && !cursorOn && root.getAttribute('data-cv-curtip') !== 'off';

  if (tipOn) {
    const tip = doc.createElement('div');
    const pill = doc.createElement('span');
    const word = doc.createElement('span');
    const ruler = doc.createElement('span');
    tip.className = 'cv-tip';
    pill.className = 'cv-tip__b';
    word.className = 'cv-tip__t';
    ruler.className = 'cv-tip__t cv-tip__ruler';
    tip.setAttribute('aria-hidden', 'true');
    pill.append(word, ruler);
    tip.appendChild(pill);
    doc.body.appendChild(tip);

    // Under the arrow's tip and clear of the hand pointer's finger.
    const GAP_X = 12;
    const GAP_Y = 26;
    const EDGE = 8;
    let mx = -100;
    let my = -100;
    let tx = 0;
    let ty = 0;
    let w = 0;
    let host = null;
    let text = '';
    let on = false;
    let wait = null;
    let swap = null;

    const spot = () => {
      const h = pill.offsetHeight;
      let x = mx + GAP_X;
      let y = my + GAP_Y;
      if (x + w > root.clientWidth - EDGE) x = mx - w - 4;
      if (y + h > window.innerHeight - EDGE) y = my - h - 10;
      return [x, y];
    };

    const tipJob = () => {
      const [x, y] = spot();
      tx = reduce ? x : tx + (x - tx) * 0.3;
      ty = reduce ? y : ty + (y - ty) * 0.3;
      tip.style.transform = `translate3d(${tx.toFixed(1)}px,${ty.toFixed(1)}px,0)`;
      return on && Math.abs(x - tx) + Math.abs(y - ty) > 0.3;
    };

    // The pill's width is set from a hidden ruler so it can ease between words.
    const setWord = (next) => {
      if (next === text) return;
      text = next;
      ruler.textContent = next;
      // + padding 11 + 12, the dot (4, a name's ring 6) and its gap 7 (cv-motion.css .cv-tip__b)
      w = Math.ceil(ruler.getBoundingClientRect().width) + (tip.hasAttribute('data-name') ? 36 : 34);
      pill.style.width = `${w}px`;
      clearTimeout(swap);
      if (!on || reduce) {
        word.textContent = next;
        return;
      }
      pill.setAttribute('data-swap', '');
      swap = setTimeout(() => {
        word.textContent = next;
        pill.removeAttribute('data-swap');
      }, 120);
    };

    const show = () => {
      if (on) return;
      on = true;
      [tx, ty] = spot();
      tipJob();
      tip.setAttribute('data-on', '');
    };

    const hide = () => {
      clearTimeout(wait);
      on = false;
      tip.removeAttribute('data-on');
    };

    const worth = (h) =>
      h.hasAttribute('data-tip') || h.hasAttribute('data-cur-show') || h.hasAttribute('data-card') || h.classList.contains('cv-btn') || !h.innerText.trim();

    const picked = (h) =>
      h.getAttribute('aria-pressed') === 'true' || h.getAttribute('aria-current') === 'true' || !!(h.control && h.control.checked);

    // Word, look and state from the element itself, so a change made by the
    // click (Save → Saved, a swatch picked) shows on the same tag.
    const dress = () => {
      const name = host.hasAttribute('data-tip');
      tip.toggleAttribute('data-dark', !!host.closest('[data-ondark]'));
      tip.toggleAttribute('data-name', name);
      tip.toggleAttribute('data-picked', name && picked(host));
      setWord(host.getAttribute(name ? 'data-tip' : 'data-cur') || text);
    };

    const sense = (el) => {
      let next = el && el.closest ? el.closest('[data-tip]:not([data-tip=""]), [data-cur]:not([data-cur=""])') : null;
      if (next && next !== host && !worth(next)) next = null;
      if (next === host) return;
      host = next;
      clearTimeout(wait);
      if (!host) {
        wait = setTimeout(hide, 90);
        return;
      }
      const go = () => {
        dress();
        show();
        run(tipJob);
      };
      if (on) go();
      else wait = setTimeout(go, 160);
    };

    const senseJob = () => {
      sense(doc.elementFromPoint(mx, my));
      return false;
    };

    window.addEventListener(
      'mousemove',
      (e) => {
        mx = e.clientX;
        my = e.clientY;
        sense(e.target);
        if (on) run(tipJob);
      },
      { passive: true }
    );
    // The page moves under a still pointer: what is under it now.
    window.addEventListener('scroll', () => { if (mx >= 0) run(senseJob); }, { passive: true });
    window.addEventListener('mousedown', () => pill.setAttribute('data-press', ''));
    window.addEventListener('mouseup', () => {
      pill.removeAttribute('data-press');
      setTimeout(() => {
        if (host && host.isConnected) dress();
        else run(senseJob);
      }, 80);
    });
    window.addEventListener('keydown', () => {
      host = null;
      hide();
    });
    doc.addEventListener('mouseout', (e) => {
      if (e.relatedTarget) return;
      host = null;
      hide();
    });
  }

  /* ---- data-zoom: press and hold (v8:L1571-1582) ------------------------- */
  // A function, not decoration: kept under reduced motion and at Motion 0.

  // On touch the press must be held (220ms, still) so a swipe or a scroll
  // that starts on the image doesn't zoom it.
  let zoomed = null;
  let hold = null;
  window.addEventListener('pointerdown', (e) => {
    if (e.button !== 0 || !e.target.closest) return;
    if (e.target.closest('button, a[href], input, select, textarea')) return;
    const z = e.target.closest('[data-zoom]');
    if (!z) return;
    const b = z.getBoundingClientRect();
    const on = () => {
      hold = null;
      z.style.setProperty('--cv-zx', (((e.clientX - b.left) / b.width) * 100).toFixed(1) + '%');
      z.style.setProperty('--cv-zy', (((e.clientY - b.top) / b.height) * 100).toFixed(1) + '%');
      z.setAttribute('data-cv-zoom', '');
      zoomed = z;
    };
    if (e.pointerType !== 'touch') return on();
    hold = { timer: setTimeout(on, 220), x: e.clientX, y: e.clientY };
  });
  window.addEventListener(
    'pointermove',
    (e) => {
      if (hold && Math.hypot(e.clientX - hold.x, e.clientY - hold.y) > 10) {
        clearTimeout(hold.timer);
        hold = null;
      }
    },
    { passive: true }
  );
  const unzoom = () => {
    if (hold) {
      clearTimeout(hold.timer);
      hold = null;
    }
    if (!zoomed) return;
    zoomed.removeAttribute('data-cv-zoom');
    zoomed = null;
  };
  window.addEventListener('pointerup', unzoom);
  window.addEventListener('pointercancel', unzoom);
  doc.addEventListener('dragstart', (e) => {
    if (e.target.closest && e.target.closest('[data-zoom]')) e.preventDefault();
  });

  /* ---- Page veil ---------------------------------------------------------- */
  // Shown on the first page of a session only: the inline script in
  // layout/theme.liquid sets html[data-cv-veil] before first paint. CSS runs
  // it; this drops the layer once it has faded.

  const veil = $('cvVeil');
  if (veil && root.hasAttribute('data-cv-veil')) {
    const lift = () => root.removeAttribute('data-cv-veil');
    veil.addEventListener('animationend', (e) => e.target === veil && lift());
    setTimeout(lift, 2400);
  }

  /* ---- Start ------------------------------------------------------------- */

  const scan = (scope = doc) => {
    // Every position is read before any data-* is written (Task 7): writing
    // an attribute the CSS selects on invalidates layout, so interleaving
    // reads and writes forced a layout per element (a 230ms task on a phone).
    const unv = within(scope, '[data-unveil]:not([data-cv-u])');
    const rev = within(scope, '[data-rev]:not([data-cv-rev])');
    const cnt = within(scope, '[data-count]:not([data-cv-c])');
    const where = (els, share) => (mo ? els.map((el) => inView(el, share)) : []);
    const unvNear = where(unv, 0.92);
    const revNear = where(rev, 0.94);
    const cntNear = where(cnt, 0.9);
    initUnveil(unv, unvNear);
    initReveal(rev, revNear);
    initCount(cnt, cntNear);
    initSplit(scope);
    last.buy = null;
    measure();
    run(frame);
  };

  bindHeader();
  if ('ResizeObserver' in window) {
    // Lazy images and late sections change the page height under the bar.
    new ResizeObserver(() => {
      measure();
      run(frame);
    }).observe(doc.body);
  }
  window.addEventListener('scroll', () => run(frame), { passive: true });
  window.addEventListener(
    'resize',
    () => {
      measure();
      run(frame);
    },
    { passive: true }
  );
  doc.addEventListener('shopify:section:load', (e) => {
    bindHeader();
    scan(e.target);
  });
  scan();

  window.CVMotion = { scan, split, run, motion: mo, reduce };
})();
